import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const generateSignature = (apiSecret, method, path, timestamp) => {
  const payload = timestamp + method + path; // Usando Web Crypto API para generar HMAC-SHA256

  const encoder = new TextEncoder();
  const key = encoder.encode(apiSecret);
  const message = encoder.encode(payload);

  return crypto.subtle
    .importKey('raw', key, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
    .then((cryptoKey) => {
      return crypto.subtle.sign('HMAC', cryptoKey, message);
    })
    .then((signature) => {
      // Convertir ArrayBuffer a hex string
      return Array.from(new Uint8Array(signature))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    });
};

const ApplicationForm = ({ jobDetails }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key]);
    });

    if (selectedFile) {
      formData.append('file', selectedFile);
    }

    // Add job details
    formData.append('idvacante', jobDetails?.id || '');
    formData.append('titulovacante', jobDetails?.titulo_vacante || '');
    formData.append('areainteres', jobDetails?.area_departamento || '');
    formData.append('linkvacante', window.location.href);
    formData.append('idioma', jobDetails?.idioma_vacante || 'español');

    try {
      const timestamp = Math.floor(Date.now() / 1000).toString();

      const signatureApplicationForm = await generateSignature(
        process.env.REACT_APP_API_SECRET, // Necesitas agregar esto en tus variables de entorno
        'POST', // Ejemplo: 'GET'
        `/postulaciones/`, // Ejemplo: '/categories/with_counts/'
        timestamp
      );

      const response = await fetch(`${API_BASE_URL}/postulaciones/`, {
        method: 'POST',
        headers: {
          'X-Timestamp': timestamp,
          'X-Signature': signatureApplicationForm,
          'X-API-Key': process.env.REACT_APP_API_KEY,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSubmitSuccess(true);
          setSelectedFile(null);
        } else {
          throw new Error('Error en la respuesta del servidor');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al enviar la postulación');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/pdf' && file.size <= 4 * 1024 * 1024) {
        setSelectedFile(file);
      } else {
        alert('Por favor, selecciona un archivo PDF menor a 4MB');
        e.target.value = '';
      }
    }
  };

  if (submitSuccess) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 text-center animate-fade-in">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg
            className="w-10 h-10 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          ¡Postulación exitosa!
        </h3>
        <p className="text-gray-600 mb-6">
          Tu postulación ha sido enviada correctamente. Revisaremos tu
          información y nos pondremos en contacto contigo pronto.
        </p>
        <a href="/vacantes" className="btn-primary">
          Ver más vacantes
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-gray-200 mb-24">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">
          ¡Postúlate a esta vacante!
        </h2>
        <p className="text-gray-600">
          Completa el formulario para enviar tu postulación
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Hidden Fields */}
        <input
          type="hidden"
          {...register('idvacante')}
          defaultValue={jobDetails?.id}
        />
        <input
          type="hidden"
          {...register('titulovacante')}
          defaultValue={jobDetails?.titulo_vacante}
        />
        <input
          type="hidden"
          {...register('areadep')}
          defaultValue={jobDetails?.area_departamento}
        />

        {/* Name Fields */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre *
            </label>
            <input
              {...register('nombre', { required: 'El nombre es requerido' })}
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all"
              placeholder="Tu nombre"
              required
            />
            {errors.nombre && (
              <p className="mt-1 text-sm text-red-600">
                {errors.nombre.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Apellidos *
            </label>
            <input
              {...register('apellidos', {
                required: 'Los apellidos son requeridos',
              })}
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all"
              placeholder="Tus apellidos"
              required
            />
            {errors.apellidos && (
              <p className="mt-1 text-sm text-red-600">
                {errors.apellidos.message}
              </p>
            )}
          </div>
        </div>

        {/* Contact Fields */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Correo electrónico *
            </label>
            <input
              {...register('email', {
                required: 'El correo es requerido',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Correo electrónico inválido',
                },
              })}
              type="email"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all"
              placeholder="tu@correo.com"
              required
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Teléfono *
            </label>
            <input
              {...register('telefono')}
              type="tel"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all"
              placeholder="(555) 123-4567"
              required
            />
          </div>
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Currículum (PDF) *
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-primary-orange transition-colors">
            <div className="space-y-1 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="flex text-sm text-gray-600">
                <label className="relative cursor-pointer bg-white rounded-md font-medium text-primary-orange hover:text-primary-blue focus-within:outline-none">
                  <span>Subir archivo</span>
                  <input
                    type="file"
                    accept=".pdf"
                    className="sr-only"
                    onChange={handleFileChange}
                    required
                  />
                </label>
                <p className="pl-1">o arrastra y suelta</p>
              </div>
              <p className="text-xs text-gray-500">PDF hasta 4MB</p>
              {selectedFile && (
                <p className="text-sm text-green-600 font-medium">
                  ✓ {selectedFile.name}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Carta de presentación (opcional)
          </label>
          <textarea
            {...register('mensaje')}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all"
            placeholder="Comparte por qué eres el candidato ideal para esta posición..."
          />
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Enviando...
              </span>
            ) : (
              'Enviar postulación'
            )}
          </button>
          <p className="mt-3 text-sm text-gray-500 text-center">
            * Campos obligatorios
          </p>
        </div>
      </form>
    </div>
  );
};

export default ApplicationForm;
